import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { getDateKeyInTimezone, resolveOrganizationTimezone } from '@/lib/timezone';
import type { Appointment } from '@/types';

// Estados en los que una cita todavia no tiene un desenlace registrado.
// 'no_show', 'completed' y 'cancelled' ya son desenlaces cerrados; 'blocked'
// no es una cita real (bloqueo de horario del profesional).
const PENDING_STATUSES = ['scheduled', 'confirmed', 'in_progress'] as const;

export type OverdueAppointmentItem = {
  /** Cita representativa del grupo (la de start_time mas temprano). */
  appointment: Appointment;
  /** Nombres de los servicios incluidos, para el resumen de la lista. */
  serviceNames: string[];
};

function getServiceSummary(members: Appointment[]): string[] {
  return members
    .map((member) => member.service?.name)
    .filter((name): name is string => Boolean(name));
}

export function useOverdueAppointments() {
  const { user } = useAuth();
  const { currentOrganization } = useTenant();
  const organizationId = user?.organization_id ?? null;
  const orgTimezone = resolveOrganizationTimezone(currentOrganization?.timezone);

  const query = useQuery({
    // Prefijo 'appointments' a proposito: asi queda cubierta por todos los
    // invalidateQueries({ queryKey: ['appointments'] }) que ya disparan hoy
    // los flujos de finalizar/cancelar/eliminar, sin tocar esos archivos.
    queryKey: ['appointments', 'overdue', organizationId],
    queryFn: async () => {
      if (!organizationId) return [] as Appointment[];

      const { data, error } = await supabase
        .from('appointments')
        .select('*, client:clients(*), service:services(*), professional:professionals(*)')
        .eq('organization_id', organizationId)
        .eq('is_blocked', false)
        .in('status', PENDING_STATUSES)
        .lt('start_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;
      return (data ?? []) as Appointment[];
    },
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  const items = useMemo<OverdueAppointmentItem[]>(() => {
    const rows = query.data ?? [];
    if (rows.length === 0) return [];

    const todayKey = getDateKeyInTimezone(new Date(), orgTimezone);
    // Solo fechas ANTERIORES a hoy — una cita de esta manana sin cerrar
    // todavia no cuenta como atrasada.
    const pastRows = rows.filter(
      (appointment) => getDateKeyInTimezone(appointment.start_time, orgTimezone) < todayKey,
    );

    // Deduplicar citas grupales/multiservicio: un solo item por group_id,
    // usando el miembro de start_time mas temprano como representante.
    const groups = new Map<string, Appointment[]>();
    for (const appointment of pastRows) {
      const key = appointment.group_id ?? appointment.id;
      const existing = groups.get(key);
      if (existing) {
        existing.push(appointment);
      } else {
        groups.set(key, [appointment]);
      }
    }

    const result: OverdueAppointmentItem[] = [];
    for (const members of groups.values()) {
      const sorted = [...members].sort(
        (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
      );
      result.push({
        appointment: sorted[0],
        serviceNames: getServiceSummary(sorted),
      });
    }

    result.sort(
      (a, b) => new Date(a.appointment.start_time).getTime() - new Date(b.appointment.start_time).getTime(),
    );

    return result;
  }, [query.data, orgTimezone]);

  return {
    items,
    count: items.length,
    isLoading: query.isLoading,
  };
}
